import {
  ColumnSort,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  Table,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"
import { DeductionExt, DeductionListJson, api_fetch, is_reportable_fetch_error } from "@/api"
import { UserState } from "@/auth/user.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import { Button } from "@/components/ui/button"
import { is_pending_task_for_user } from "@/api/task.tsx"
import { UserCheck } from "lucide-react"
import { TableControls } from "@/deduction_table/table_controls.tsx"
import { INITIAL_TABLE_STATE, TableState } from "@/deduction_table/table_state.tsx"
import { capitalCase } from "change-case"
import { PageControls } from "@/deduction_table/page_controls.tsx"
import { DeductionTableSelectionBar } from "./DeductionTableSelectionBar.tsx"
import { BulkWizardSidebar } from "./BulkWizardSidebar.tsx"
import { dateFilter, defaultFilter, fuzzyFilter } from "@/utils/filters.ts"
import { DeductionNavbar, GlobalFilters } from "@/app/navbar"
import { INVOICE_COLUMNS_WITHOUT_BACKUP_PARSE } from "@/dashboard/sales/sales_columns.tsx"
import { AgingPanel } from "@/deduction/aging/AgingPanel.tsx"
import { AgingPanelState } from "@/deduction/aging/aging_state"
import { TableBreakdowns } from "@/deduction/TableBreakdowns.tsx"
import { DeductionAlerts, computeDeductionAlerts } from "@/deduction/DeductionAlerts.tsx"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { BASE_COLUMNS_WITHOUT_BACKUP_PARSE, backupParseColumn } from "@/deduction_table/columns.tsx"
import { createOrgSpecificColumns } from "@/deduction_table/org_columns.tsx"
import { GlobalFilterState } from "@/global_filter"
import { OrgConfigState, loadOrgFieldConfigs } from "@/org_config/state.tsx"
import { map, toISODateString, useAsyncEffect, usePageTitle } from "@/utils"
import { useMemo, useState } from "preact/compat"
import { BulkWizardOpen, closeBulkWizard } from "./bulk_wizard_state"
import { openDeductionDetailModal, DeductionDetailModalId } from "./deduction_detail_modal_state"
import { FILTERED_TABLE_ANCHOR_ID } from "@/utils/filter_feedback"
import { assigned_to_me_summary } from "./assigned_to_me"

export function handleDeductionTableSearch(value: string) {
  TableState.set(s => {
    let pagination = s.pagination
    pagination.pageIndex = 0
    return { ...s, search: value, pagination }
  })
}

function toggleAssignedToMe() {
  TableState.set(s => ({
    ...s,
    assignedToMe: !s.assignedToMe,
    rowSelection: {},
    pagination: { ...s.pagination, pageIndex: 0 },
  }))
}

export function DeductionTable() {
  usePageTitle("Deductions")
  let user = UserState.use(u => u!)
  let tableState = TableState.use(ts => ts!)
  let [loading, setLoading] = useState(true)
  let [serverData, setServerData] = useState<DeductionListJson[]>([])
  let orgConfig = OrgConfigState.use()
  let statusFlag = RefreshFlag.use(sf => sf!)
  let bulkWizardOpen = BulkWizardOpen.use()
  let modalDeductionId = DeductionDetailModalId.use((id: string | null) => id)

  const { startDate, endDate, distributor, dateKind } = GlobalFilterState.use(gf => gf!)
  // Re-render the table when the Aging panel age basis changes so Days Old sort/accessor refresh.
  AgingPanelState.use(s => s.basis)

  useAsyncEffect(async () => {
    setServerData([])
    setLoading(true)
    await loadOrgFieldConfigs()
    const res = await api_fetch<DeductionListJson[]>("/v2/deduction/list", {
      method: "POST",
      body: {
        start_date: map(startDate, toISODateString),
        end_date: map(endDate, toISODateString),
        distributor,
        date_kind: dateKind,
        assigned_to_me: tableState.assignedToMe,
      },
    })
    setLoading(false)
    if (!res.ok) {
      if (is_reportable_fetch_error(res.error)) {
        throw res.error
      }
      return
    }
    setServerData(res.value.data)
    TableState.set(s => {
      let pagination = s.pagination
      pagination.pageIndex = 0
      return { ...s, pagination }
    })
  }, [startDate, endDate, distributor, dateKind, tableState.assignedToMe])

  useAsyncEffect(async () => {
    const res = await api_fetch<DeductionListJson[]>("/v2/deduction/list", {
      method: "POST",
      body: {
        start_date: map(startDate, toISODateString),
        end_date: map(endDate, toISODateString),
        distributor,
        date_kind: dateKind,
        assigned_to_me: tableState.assignedToMe,
      },
    })
    if (res.ok) {
      setServerData(res.value.data)
    }
  }, [statusFlag])

  // Determine which columns to use
  let columns = useMemo(() => {
    const baseColumns = tableState.earlyPay ? INVOICE_COLUMNS_WITHOUT_BACKUP_PARSE : BASE_COLUMNS_WITHOUT_BACKUP_PARSE
    const orgColumns = createOrgSpecificColumns(user.org_id!, orgConfig.orgFieldConfigs)
    return [...baseColumns, ...orgColumns, backupParseColumn]
  }, [tableState.earlyPay, user.org_id, orgConfig.orgFieldConfigs])

  let alerts = useMemo(() => computeDeductionAlerts(serverData), [serverData])

  let data = useMemo(() => {
    let filteredData = serverData
    if (tableState.earlyPay) {
      filteredData = filteredData.filter(d => d.kind === "invoice")
    }
    return filteredData
  }, [serverData, tableState.earlyPay])

  const table = useMemo(() => {
    return useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      // NOTE: Tanstack has very *surprising* default behavior, where it ignores filtering a column if the column's value in the first row is null
      // This lead to bugs like category_description sometimes not being filtered (i.e. when by coincidence the first row has a null value).
      // I'm fixing this in two ways, first by making category_description be string instead of string | null, and
      // second by defining getColumnCanGlobalFilter in this config, which eliminates the "check the value in the first row" logic.
      //
      // It can also be fixed by defining an accessorFn that always returns a string (i.e. does coercion)
      //
      // Quite grotesquely, by default, setting the column config option enableGlobalFilter to true does not override the "guessing" behavior above.
      // Our implementation of getColumnCanGlobalFilter overrides the default behavior so that it behaves as you'd expect.
      // There are six open github issues for the filtering based on first row issue & the enableGlobalFilter config issue, because it's that stupid:
      // https://github.com/TanStack/table/issues/4711
      // https://github.com/TanStack/table/discussions/5084
      // https://github.com/TanStack/table/issues/5138
      // https://github.com/TanStack/table/issues/4783
      // https://github.com/TanStack/table/issues/4919
      // https://github.com/TanStack/table/issues/4594

      // this is set to false because we want to maintain the current page if we take an action. we only want to reset the page if we change the data (global filter or column filter or early pay)
      autoResetPageIndex: false,
      getColumnCanGlobalFilter: c => c.columnDef.enableGlobalFilter ?? true,
      globalFilterFn: fuzzyFilter,
      filterFns: {
        dateFilter: dateFilter,
        default: defaultFilter,
      },
      onColumnFiltersChange: function (updater: any) {
        TableState.set(s => {
          let columnFilters = updater(s.columnFilters)
          let pagination = s.pagination
          pagination.pageIndex = 0
          return { ...s, columnFilters, pagination }
        })
      },
      onColumnVisibilityChange: function (updater: any) {
        const visibility = updater(tableState.columnVisibility!) as VisibilityState
        TableState.set({ ...tableState, columnVisibility: visibility })
      },
      onGlobalFilterChange: handleDeductionTableSearch,
      onRowSelectionChange: function (updater: any) {
        const selection = updater(tableState.rowSelection!) as RowSelectionState
        TableState.set({ ...tableState, rowSelection: selection })
      },
      onSortingChange: function (updater: any) {
        let new_sort = updater(tableState.sorting!) as SortingState
        new_sort.splice(0, 0, { id: "validated" } as ColumnSort)
        TableState.set(s => ({
          ...s,
          sorting: new_sort,
        }))
      },
      onPaginationChange: function (updater: any) {
        let new_pagination = updater(tableState.pagination!) as PaginationState
        TableState.set({ ...tableState, pagination: new_pagination })
      },
      state: {
        columnVisibility: tableState.columnVisibility,
        rowSelection: tableState.rowSelection as RowSelectionState,
        sorting: tableState.sorting,
        globalFilter: tableState.search,
        pagination: tableState.pagination,
        columnFilters: tableState.columnFilters,
      },
      meta: {
        resetRowSelection: () => {
          TableState.set(s => ({ ...s, rowSelection: {} }))
        },
        clearAllFilters: () => {
          TableState.set(current => ({
            ...INITIAL_TABLE_STATE,
            columnVisibility: current.columnVisibility,
            sorting: current.sorting,
            // earlyPay: current.earlyPay, // Preserve early pay state
          }))
        },
      },
    })
  }, [data, columns, tableState])

  const isRowSelected = tableState.rowSelection && Object.keys(tableState.rowSelection).length > 0

  function handleRowClick(row: Row<DeductionExt>, event: MouseEvent, table: Table<DeductionListJson>) {
    if (table.getIsSomeRowsSelected()) {
      row.toggleSelected()
      return
    }
    let ded = row.original
    if (event.metaKey) {
      window.open(`/deduction/${ded.id}`, "_blank")
    } else {
      openDeductionDetailModal(ded.id)
    }
  }

  // Get selected deductions for bulk wizard
  const selectedRows = table.getFilteredSelectedRowModel().rows
  const selectedDeductions = selectedRows.map((row: any) => row.original as DeductionListJson)

  let rows = table.getRowModel().rows
  return (
    <div>
      <div className="px-4">
        <DeductionNavbar>
          <GlobalFilters />
        </DeductionNavbar>
      </div>
      <div className="px-4">
{user.role === "system_admin" && <DeductionAlerts alerts={alerts} />}
        <AgingPanel data={serverData} />
        <TableBreakdowns data={serverData} />
      </div>
      <div id={FILTERED_TABLE_ANCHOR_ID} className="mx-auto mb-16 scroll-mt-4">
        <TableControls
          table={table}
          quickFilters={
            <Button
              variant={tableState.assignedToMe ? "default" : "outline"}
              className={tableState.assignedToMe ? "" : "text-slate-500"}
              aria-pressed={tableState.assignedToMe}
              onClick={toggleAssignedToMe}>
              <UserCheck className="h-4" />
              Assigned to me
            </Button>
          }
        />
        {tableState.assignedToMe && !loading && (
          <p class="px-4 pb-2 text-sm text-slate-600" role="status">
            {assigned_to_me_summary(serverData.length, !!distributor)}
          </p>
        )}
        <div class="border w-full">
          <div class="overflow-auto w-full">
            <table class="w-full border-collapse" style={{ tableLayout: "auto" }}>
              <thead class="bg-slate-50">
                {table.getHeaderGroups().map((headerGroup, idx) => (
                  <tr key={`${headerGroup.id}-${idx}`} className="border-b">
                    {headerGroup.headers.map((header, headerIdx) => (
                      <th
                        key={`${header.id}-${headerIdx}`}
                        className="px-1 py-1 text-left text-xs font-medium text-slate-700 whitespace-nowrap"
                        style={{
                          position: "sticky",
                          top: 0,
                          backgroundColor: "#f8fafc",
                          zIndex: 10,
                          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                          maxWidth: "180px",
                          width: "auto",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={header.column.id ? capitalCase(header.column.id) : ""}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    {Array.from({ length: columns.length }).map((_, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-3 border-b">
                        <Skeleton className="w-[100px] h-[20px] rounded-full bg-slate-500" />
                      </td>
                    ))}
                  </tr>
                ) : rows.length ? (
                  rows.map((row, idx) => {
                    let task = row.original.task
                    let isModalOpen = modalDeductionId === row.original.id
                    return (
                      <tr
                        key={`${row.id}-${idx}`}
                        className={`hover:bg-slate-50 ${
                          isModalOpen
                            ? "bg-blue-100"
                            : is_pending_task_for_user(task, user)
                              ? "bg-blue-100"
                              : ""
                        }`}
                        onClick={event => handleRowClick(row as any, event, table)}
                        data-state={row.getIsSelected() && "selected"}>
                        {row.getVisibleCells().map((cell, cellIdx) => (
                          <td
                            className="px-1 py-1 border-b whitespace-nowrap text-xs"
                            style={{
                              maxWidth: "180px",
                              width: "auto",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={String(cell.getValue() || "")}
                            key={`${cell.id}-${cellIdx}`}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="h-24 text-center">
                      No results.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {isRowSelected && <DeductionTableSelectionBar table={table} isAccounting={false} orgFieldConfigs={orgConfig.orgFieldConfigs} />}
        <div class="flex items-center justify-between p-2 px-4">
          <PageControls table={table} />
        </div>
      </div>

      {/* Bulk Wizard Sidebar - always available */}
      <BulkWizardSidebar
        open={bulkWizardOpen}
        onOpenChange={(open) => {
          if (!open) closeBulkWizard()
        }}
        selectedDeductions={selectedDeductions}
        onComplete={() => {
          RefreshFlag.set(f => !f)
          TableState.set(s => ({ ...s, rowSelection: {} }))
        }}
      />
    </div>
  )
}
